import { Button } from '@helios/frontend-core';
import { PalmettoIcon } from '@palmetto/dsm-components';
import React, { RefObject } from 'react';

export interface QuestionListProps {
  questions: string[];
  trackingId: string;
  isExpanded: boolean;
  /** The question rows' own natural height, *excluding* any padding - `contentRef` below is
   * scoped to just the rows so this stays stable regardless of the bottom padding below. */
  height: number;
  contentRef: RefObject<HTMLDivElement>;
  onSelectQuestion: (question: string) => void;
}

export const QuestionList: React.FC<QuestionListProps> = ({ questions, trackingId, isExpanded, height, contentRef, onSelectQuestion }) => {
  const shouldReveal = isExpanded && questions.length > 0;
  return (
    <div
      aria-hidden={!isExpanded}
      // `bottom` (27px - half the collapsed bar's 54px height, a point the card always
      // covers) is fixed at all times, collapsed or expanded - it never animates. `height` is
      // the rows' own stable measured height plus the 113px of padding around them (the
      // `pt-20` + `pb-[93px]` on the inner div below: 20px content padding above, and below
      // half the expanded card's 98px resting height + 24px of its `-mt-32` overlap + 20px
      // content padding = 93). `height` is the *only* thing that animates here, from 0
      // (collapsed) up to its full amount (expanded), at the same duration and with no delay
      // as every other part of this expand/collapse. All the paddings are static,
      // hand-computed from the card's own fixed layout, never measured live - a
      // ResizeObserver reported mid-animation heights and made this list visibly "pop" on
      // close. Recompute by hand if the card's padding/border/row structure ever changes.
      style={{ height: shouldReveal ? height + 113 : 0 }}
      className="absolute inset-x-0 bottom-[27px] overflow-hidden rounded-t-24 bg-palmetto-600 transition-[height] duration-300 ease-out"
    >
      <div className="absolute inset-x-0 bottom-0 px-20 pb-[93px] pt-20">
        {/* Scoped to just the rows, deliberately excluding the padding above - see `height`'s
            own comment on why that padding can't be part of what this measures. */}
        <div ref={contentRef} className="flex flex-col gap-16">
          {questions.map((question, index) => (
            <Button
              key={question}
              trackingId={`${trackingId}-question-${index}`}
              emphasis="text"
              layout="hug"
              onClick={() => onSelectQuestion(question)}
              // Icon (`sm`->`xs`, 16px->12px - see `PalmettoIcon`'s own size map) and margin
              // (9.6px->8px, the same ~0.83 ratio) both shrunk alongside the question text's
              // own smaller `body-300` scale, so the icon stays proportional to the text
              // beside it rather than reading as oversized once the text shrank.
              leadingIcon={
                <span className="mr-8">
                  <PalmettoIcon name="lineArrowCurveDownRight" size="xs" aria-hidden="true" />
                </span>
              }
              tabIndex={isExpanded ? 0 : -1}
              // `max-w-full` caps `layout="hug"`'s own shrink-to-content width at the row's
              // available width, so `truncate` (`overflow-hidden`/`whitespace-nowrap`/
              // `text-ellipsis`) has something to actually clip against instead of just
              // growing the button past its container. `children` still has to stay a plain
              // string below, not a wrapping span - `Button` itself checks
              // `typeof children === 'object'` to decide whether children is an *icon* rather
              // than text, and switches to icon-button styling (silently resetting
              // `emphasis="text"` to `"primary"`, with much larger icon-button padding) the
              // moment that check passes - truncating via a wrapping element caused exactly
              // that regression once already.
              className="sunny-prepopulated-question-trigger !text-grey-50 !body-300 !max-w-full !justify-start truncate text-left"
            >
              {question}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
